import printConfigService from '@/services/PrintConfigService';
import { AxiosError } from 'axios';
import { defineStore } from 'pinia';
import { useStore as useShapeStore } from './shape';
import { useStore as useColorsManagmentStore } from './colorsManagement';
import { useStore as useNotificationStore } from './notification';
import { useStore as useMirrorStore } from './mirror';
import {
  ProductPrintPositionAddModel,
  ProductPrintPositionBoxModel,
} from '../typings';
import { mm2px } from '../utils';
import { AnnotationShape } from '@/web-components/import-wizard/typings';
import { Polygon, Vector } from 'collider2d';
import { isConvex } from '@/utils/collisionHelper';
import { lineWidth } from '@/constants/areaLineWidth';
import { ProductSnapshot } from '@/typings/history';
import hexRgb from 'hex-rgb';
import {
  ProductVariant as SelectedProductVariant,
  ProductPrintAreaPoint,
  ProductPrintArea,
  PmsColor,
  ProductPrintPosition,
  ProductVariantImage,
  ProductPrintVariant,
  ProductPrintData,
  LaserConfiguration,
} from '@midocean/webapps-shared-domain';
import { getTechniqueGroupCode } from '@/utils/validationHelper';
import { PRINTING_GROUPS } from '@/constants/printTechnique';

export const useStore = defineStore({
  id: 'product',

  state: () => {
    return {
      data: {} as ProductPrintData,
      errors: [] as AxiosError[],
      unavailablePrintPositions: [] as ProductPrintPositionAddModel[],
      selectedPrintPosition: {} as ProductPrintPositionBoxModel,
      lastManagedPrintPositionId: '',
      previousManagedPrintPositionId: '',
      positionsSetFromProps: false,
      selectedVariants: [] as SelectedProductVariant[],
      selectedVariant: {} as ProductPrintVariant,
      isProductPrintable: true,
      editedPositions: [] as string[],
      isProcessingOrder: false,
    };
  },

  getters: {
    activePosition: (state) => {
      if (!state.data.id) {
        return {};
      }
      const ppIndex = state.data.productPrintPositions.findIndex((ppp) => {
        return ppp.id === state.selectedPrintPosition?.id;
      });
      if (ppIndex === -1) return {};
      const {
        id,
        originalImageWebDpi,
        name,
        printArea,
        originalImageWidthPx,
        originalImageHeightPx,
        originalImageDpi,
        printTechniques,
        categoryConfiguration,
        category,
      } = state.data.productPrintPositions?.[ppIndex > -1 ? ppIndex : 0];

      let { originalImageUrl } =
        state.data.productPrintPositions?.[ppIndex > -1 ? ppIndex : 0];

      if (state.selectedVariant && state.selectedVariant.images) {
        const imageUrl = state.selectedVariant.images.find((img) => {
          return id === img.printPositionID;
        }) as ProductVariantImage;
        originalImageUrl = imageUrl.imageUrl;
      }

      const limitation = printTechniques.find(
        (x) => x.id == state.selectedPrintPosition.techniqueId
      )?.printTechnique.limitation;

      return {
        id,
        originalImageUrl,
        originalImageDpi: originalImageDpi === 0 ? 96 : originalImageDpi,
        originalImageWebDpi:
          originalImageWebDpi === 0 ? 96 : originalImageWebDpi,
        name,
        printArea,
        originalImageWidthPx,
        originalImageHeightPx,
        limitation,
        categoryConfiguration,
        category,
      };
    },
    isLaserConverter: (state) => {
      return (printPositionId?: string, techCode?: string) => {
        if (techCode !== undefined) {
          const printPostion = state.data.productPrintPositions?.find(
            (p) => p.id === printPositionId
          );
          const technique = printPostion?.printTechniques.find(
            (t) => t.printTechnique.code === techCode
          );

          return getTechniqueGroupCode(technique, PRINTING_GROUPS.LASER_GROUPS);
        } else {
          // if printPositionId is not defined, then we use the one from selectedPrintPosition
          let posId = state.selectedPrintPosition.id;
          let techniqueId = state.selectedPrintPosition.techniqueId;

          if (printPositionId !== undefined) {
            posId = printPositionId;
            techniqueId = state.unavailablePrintPositions.find(
              (pp) => pp.positionId === posId
            )?.technique.id;
          }

          const printPostion = state.data.productPrintPositions?.find(
            (p) => p.id === posId
          );
          const technique = printPostion?.printTechniques.find(
            (t) => t.id === techniqueId
          );
          return getTechniqueGroupCode(technique, PRINTING_GROUPS.LASER_GROUPS);
        }
      };
    },
    isCeramicTransfer: (state) => {
      return (printPositionId?: string, techCode?: string) => {
        if (techCode !== undefined) {
          const printPostion = state.data.productPrintPositions?.find(
            (p) => p.id === printPositionId
          );
          const technique = printPostion?.printTechniques.find(
            (t) => t.printTechnique.code === techCode
          );
          return getTechniqueGroupCode(
            technique,
            PRINTING_GROUPS.CERAMIC_GROUPS
          );
        } else {
          // if printPositionId is not defined, then we use the one from selectedPrintPosition
          let posId = state.selectedPrintPosition.id;
          let techniqueId = state.selectedPrintPosition.techniqueId;

          if (printPositionId !== undefined) {
            posId = printPositionId;
            techniqueId = state.unavailablePrintPositions.find(
              (pp) => pp.positionId === posId
            )?.technique.id;
          }

          const printPostion = state.data.productPrintPositions?.find(
            (p) => p.id === posId
          );
          const technique = printPostion?.printTechniques.find(
            (t) => t.id === techniqueId
          );
          return getTechniqueGroupCode(
            technique,
            PRINTING_GROUPS.CERAMIC_GROUPS
          );
        }
      };
    },
    isFullColor: (state) => {
      return (printPositionId?: string) => {
        // if printPositionId is not defined, then we use the one from selectedPrintPosition
        let posId = state.selectedPrintPosition.id;
        let techniqueId = state.selectedPrintPosition.techniqueId;

        if (printPositionId !== undefined) {
          posId = printPositionId;
          techniqueId = state.unavailablePrintPositions.find(
            (pp) => pp.positionId === posId
          )?.technique.id;
        }
        const printPostion = state.data.productPrintPositions?.find(
          (p) => p.id === posId
        );
        const technique = printPostion?.printTechniques.find(
          (t) => t.id === techniqueId
        );

        return technique?.printTechnique.isFullColor || false;
      };
    },
    isPantoneColorTechnique() {
      return (printPositionId?: string) => {
        // if printPositionId is not defined, then we use the one from selectedPrintPosition
        let posId = this.selectedPrintPosition.id;
        let techniqueId = this.selectedPrintPosition.techniqueId;

        if (printPositionId !== undefined) {
          posId = printPositionId;
          techniqueId =
            this.unavailablePrintPositions.find((pp) => pp.positionId === posId)
              ?.technique.id || '';
        }
        const printPostion = this.data.productPrintPositions?.find(
          (p) => p.id === posId
        );
        const technique = printPostion?.printTechniques.find(
          (t) => t.id === techniqueId
        );

        if (technique) {
          return (
            !this.isFullColor() &&
            !this.isLaserConverter() &&
            !this.isDebossingTechnique() //&&
            //!this.isSublimationTechnique
          );
        } else {
          return false;
        }
      };
    },
    isSmallDetailsTechnique: (state) => {
      return (positionId?: string, techCode?: string) => {
        if (!positionId && !techCode) {
          const currentPrintPosition = state.data.productPrintPositions?.find(
            (p) => p.id === state.selectedPrintPosition.id
          );
          const technique = currentPrintPosition?.printTechniques.find(
            (t) => t.id === state.selectedPrintPosition.techniqueId
          );

          return technique?.printTechnique.checkSmallDetails || false;
        }

        const printPostion = state.data.productPrintPositions?.find(
          (p) => p.id === positionId
        );
        const technique = printPostion?.printTechniques.find(
          (t) => t.printTechnique.code === techCode
        );

        return technique?.printTechnique.limitation !== null || false;
      };
    },
    isSublimationTechnique: (state) => {
      const currentPrintPosition = state.data.productPrintPositions?.find(
        (p) => p.id === state.selectedPrintPosition.id
      );

      const technique = currentPrintPosition?.printTechniques.find(
        (t) => t.id === state.selectedPrintPosition.techniqueId
      );

      return getTechniqueGroupCode(
        technique,
        PRINTING_GROUPS.SUBLIMATION_GROUPS
      );
    },
    isDebossingTechnique: (state) => {
      return (printPositionId?: string, techCode?: string) => {
        if (techCode !== undefined) {
          const printPostion = state.data.productPrintPositions?.find(
            (p) => p.id === printPositionId
          );
          const technique = printPostion?.printTechniques.find(
            (t) => t.printTechnique.code === techCode
          );

          return getTechniqueGroupCode(
            technique,
            PRINTING_GROUPS.DEBOSSING_GROUPS
          );
        } else {
          // if printPositionId is not defined, then we use the one from selectedPrintPosition
          let posId = state.selectedPrintPosition.id;
          let techniqueId = state.selectedPrintPosition.techniqueId;

          if (printPositionId !== undefined) {
            posId = printPositionId;
            techniqueId = state.unavailablePrintPositions.find(
              (pp) => pp.positionId === posId
            )?.technique.id;
          }

          const printPostion = state.data.productPrintPositions?.find(
            (p) => p.id === posId
          );
          const technique = printPostion?.printTechniques.find(
            (t) => t.id === techniqueId
          );
          return getTechniqueGroupCode(
            technique,
            PRINTING_GROUPS.DEBOSSING_GROUPS
          );
        }
      };
    },
    getDebossingColor: (state) => {
      const variantImage = state.selectedVariant.images?.find(
        (i) => i.printPositionID == state.selectedPrintPosition.id
      );
      if (variantImage && variantImage.mostUsedColor) {
        const debossingColor = hexRgb(variantImage.mostUsedColor);
        return [debossingColor.red, debossingColor.green, debossingColor.blue];
      }
      return [];
    },
    area: (state) => {
      return (positionId?: string) => {
        let currentPosition: ProductPrintPosition = {} as ProductPrintPosition;

        const printPositions = state.data.productPrintPositions?.filter(
          (position) => {
            return state.unavailablePrintPositions.some((p) => {
              return p.positionId === position.id;
            });
          }
        );
        if (
          positionId === undefined &&
          state.selectedPrintPosition.id !== undefined
        ) {
          const activePosition = printPositions?.find((p) => {
            return p.id === state.selectedPrintPosition.id;
          });
          currentPosition = activePosition as ProductPrintPosition;
        } else {
          currentPosition = printPositions?.find((p) => {
            return p.id === positionId;
          }) as ProductPrintPosition;
        }

        if (!currentPosition) return [];

        const { printArea, originalImageWebDpi } = currentPosition;
        const points: ProductPrintAreaPoint[] = (printArea as ProductPrintArea)
          ?.productPrintAreaPoints;

        if (!points) return [];

        return [...points]
          .sort((a, b) => (a.sequenceNo > b.sequenceNo ? 1 : -1))
          .map((point: ProductPrintAreaPoint) => {
            return {
              x: mm2px(
                point['distanceFromLeftEdge'],
                originalImageWebDpi === 0 ? 96 : originalImageWebDpi
              ),
              y: mm2px(
                point['distanceFromTop'],
                originalImageWebDpi === 0 ? 96 : originalImageWebDpi
              ),
            };
          });
      };
    },

    rotation(): number {
      const printArea = this.activePosition.printArea as ProductPrintArea;
      if (printArea) {
        const rotationInDegrees = printArea.rotation as number;
        const rotationInRadians = (rotationInDegrees * Math.PI) / 180;
        return rotationInRadians;
      }
      return 0;
    },

    printPositions: (state): ProductPrintPosition[] => {
      return state.data.productPrintPositions?.filter((position) => {
        return state.unavailablePrintPositions.some((p) => {
          return p.positionId === position.id;
        });
      });
    },
  },

  actions: {
    resetProductStateToDefault() {
      this.$patch((state) => {
        state.data = {} as ProductPrintData;
        state.errors = [] as AxiosError[];
        state.unavailablePrintPositions = [] as ProductPrintPositionAddModel[];
        state.selectedPrintPosition = {} as ProductPrintPositionBoxModel;
        state.lastManagedPrintPositionId = '';
        state.previousManagedPrintPositionId = '';
        state.positionsSetFromProps = false;
        state.selectedVariants = [] as SelectedProductVariant[];
        state.selectedVariant = {} as ProductPrintVariant;
        state.isProductPrintable = true;
        state.editedPositions = [] as string[];
        state.isProcessingOrder = false;
      });
    },
    polygonDetails(positionId?: string) {
      const vectors = [];
      for (let i = 0; i < this.area(positionId).length; i++) {
        vectors.push(
          new Vector(this.area(positionId)[i]?.x, this.area(positionId)[i]?.y)
        );
      }
      const polygon = new Polygon(new Vector(0, 0), vectors);
      const centroid = polygon.getCentroid();

      let min = Number.MAX_VALUE;

      if (!isConvex(polygon)) {
        const points = [...polygon.points];
        for (let i = 0; i < points.length; i++) {
          const point = points[i].clone();
          const distanceToPoint = point.sub(centroid).len();
          if (distanceToPoint < min) {
            min = distanceToPoint;
          }
        }
      } else {
        const points = [...polygon.points];
        for (let i = 0; i < points.length; i++) {
          let next = i + 1;
          if (next >= points.length) {
            next = 0;
          }

          // calculate slope ---> (y2-y1)/(x2-x1)
          const k =
            (points[next].y - points[i].y) / (points[next].x - points[i].x);

          // calcualte n --->  y = kx + n; n = y - kx;
          const n = points[i].y - k * points[i].x;

          // transform to Ax + By + C = 0 --> - kx + y - n = 0;
          const a = -k;
          const b = 1;
          const c = -n;

          // calculate distance ---> d = abs(a*x0 + b*y0 + c) / sqrt(a*a + b*b)
          const distanceToSide = Math.abs(
            (a * centroid.x + b * centroid.y + c) / Math.sqrt(a * a + b * b)
          );
          if (distanceToSide < min) {
            min = distanceToSide;
          }
        }
      }
      return {
        x: centroid.x,
        y: centroid.y,
        minDistance: min * 1.5,
      };
    },
    drawPoints(positionId?: string): any | undefined {
      if (!this.area(positionId).length) return;

      let currentPosition: ProductPrintPosition = {} as ProductPrintPosition;
      if (positionId === undefined) {
        currentPosition = this.activePosition as ProductPrintPosition;
      } else {
        currentPosition = this.printPositions.find(
          (position) => position.id === positionId
        ) as ProductPrintPosition;
      }
      const {
        printArea,
        originalImageWebDpi,
        originalImageWidthPx,
        originalImageDpi,
      } = currentPosition;
      if (printArea?.shapeType === 'Rectangle') {
        const webImageWidthPx =
          (originalImageWidthPx * originalImageWebDpi) / originalImageDpi;
        const rectLineWidth = (lineWidth * webImageWidthPx) / 100;

        return {
          x: this.area(positionId)[0]?.x - rectLineWidth / 2,
          y: this.area(positionId)[0]?.y - rectLineWidth / 2,
          w: mm2px(printArea.widthMm, originalImageWebDpi) + rectLineWidth,
          h: mm2px(printArea.heightMm, originalImageWebDpi) + rectLineWidth,
        };
      }
      if (printArea?.shapeType === 'Ellipse') {
        return {
          x: (this.area(positionId)[0].x + this.area(positionId)[1].x) / 2,
          y: (this.area(positionId)[0].y + this.area(positionId)[1].y) / 2,
          rx: (this.area(positionId)[1].x - this.area(positionId)[0].x) / 2,
          ry: (this.area(positionId)[1].y - this.area(positionId)[0].y) / 2,
        };
      }
      if (printArea?.shapeType === 'Polygon') {
        return [...this.area(positionId), this.area(positionId)[0]];
      }
    },
    async getProduct(productSKU: string): Promise<ProductPrintData> {
      try {
        const { data } = await printConfigService.get(productSKU);
        this.data = data;
        return data;
      } catch (e) {
        this.errors.push(e as AxiosError);
        throw e;
      }
    },

    setSelectedVariant(variant: ProductPrintVariant) {
      this.selectedVariant = variant;
    },

    setSelectedVariants(variants: SelectedProductVariant[]) {
      this.selectedVariants = variants;
    },

    getVariantLaserColor(
      printPositionId: string,
      techniqueId: string,
      variantSku: string
    ) {
      const printPosition = this.data.productPrintPositions.find(
        (p) => p.id === printPositionId
      );

      const printTechnique = printPosition?.printTechniques.find(
        (t) => t.id === techniqueId
      );

      const converters = printTechnique?.printTechnique.converters;

      const eskoConverters = printTechnique?.printTechnique.eskoConverters;

      const baseLaser = converters?.find(
        (c) => c.type === 'Laser' && c.variantSku === null
      );

      let laserConfiguration: LaserConfiguration = {} as LaserConfiguration;

      let eskoLaserPmsColor: PmsColor | undefined = undefined;

      const eskoLaserConverter = eskoConverters?.find(
        (c) =>
          c.type === 'Laser' &&
          c.variantSku === variantSku &&
          c.positionName === printPosition?.name
      );

      if (eskoLaserConverter !== undefined) {
        const eskoLaserConfig = JSON.parse(eskoLaserConverter.configuration);
        const colorManagementStore = useColorsManagmentStore();

        eskoLaserPmsColor = colorManagementStore.availablePmsColors.find(
          (x) => x.name === eskoLaserConfig.laserColor
        );

        if (eskoLaserPmsColor) {
          laserConfiguration.laserColor = {
            red: eskoLaserPmsColor.red,
            blue: eskoLaserPmsColor.blue,
            green: eskoLaserPmsColor.green,
          };

          laserConfiguration.laserPercentage = eskoLaserConfig.laserPercentage;
        }
      }
      if (eskoLaserPmsColor === undefined) {
        const configString =
          converters?.find(
            (c) => c.type === 'Laser' && c.variantSku === variantSku
          )?.configuration || baseLaser?.configuration;

        laserConfiguration = JSON.parse(configString as string);
      }

      return laserConfiguration;
    },

    async deletePrintPosition(id: string) {
      if (!this.unavailablePrintPositions.some((p) => p.positionId === id)) {
        console.warn('Invalid id provided. Cannot delete entity.');
        return;
      }
      /**
       * If we just deleted the last managed remove the reference as well
       */
      if (this.lastManagedPrintPositionId === id) {
        this.lastManagedPrintPositionId = '';
      }
      const removeIndex = this.unavailablePrintPositions.findIndex(
        (p) => p.positionId === id
      );

      /**
       * Remove item from list of unavailable print positions,
       * thus making it available again
       */
      this.unavailablePrintPositions.splice(removeIndex, 1);

      /**
       * If we still have some in list show/select the first one
       */
      if (this.unavailablePrintPositions.length > 0) {
        this.setActivePrintPosition(this.unavailablePrintPositions[0]);
      } else {
        /**
         * Other wise just reset the dropdown box
         */
        this.setActivePrintPosition({} as ProductPrintPositionAddModel);
      }
      const exists = this.editedPositions.some((p) => p === id);
      if (!exists) {
        this.editedPositions.push(id);
      }

      const notificationStore = useNotificationStore();
      notificationStore.resetNotificationsForBell(id);

      const mirrorStore = useMirrorStore();
      mirrorStore.removeMirrorShape(id);
    },

    setActivePrintPosition(position: ProductPrintPositionAddModel) {
      const activePosition = this.printPositions?.find((p) => {
        return p.id === position.positionId;
      });

      if (!activePosition) {
        this.selectedPrintPosition = {} as ProductPrintPositionBoxModel;
      } else {
        this.selectedPrintPosition = {
          id: activePosition.id,
          name: activePosition.name,
          originalImageUrl: activePosition.originalImageUrl,
          originalImageDpi: activePosition.originalImageDpi,
          originalImageWebUrl: activePosition.originalImageWebUrl,
          originalImageWebDpi: activePosition.originalImageWebDpi,
          techniqueName: position.technique.techniqueName,
          techniqueId: position.technique.id,
          maximumColours: activePosition.printTechniques.find((technique) => {
            return technique.id === position?.technique.id;
          })?.maximumColours,
          maxWidthMm: activePosition.maxWidthMm,
          maxHeightMm: activePosition.maxHeightMm,
        };
        this.lastManagedPrintPositionId = activePosition.id;
      }
    },

    setUnavailablePrintPositions(positions: ProductPrintPositionAddModel[]) {
      this.unavailablePrintPositions = [...positions];
    },

    setIsProductPrintable(isPrintable: boolean) {
      this.isProductPrintable = isPrintable;
    },
    setIsProcessingOrder(isProcessing: boolean) {
      this.isProcessingOrder = isProcessing;
    },
    setPreviousManagedPrintPositionId(id: string) {
      this.previousManagedPrintPositionId = id;
    },
    updateProductStateFromSnapshot(snapshot: ProductSnapshot) {
      this.$patch((state) => {
        if (snapshot !== undefined) {
          state.unavailablePrintPositions = snapshot.unavailablePrintPositions;
          state.selectedPrintPosition = snapshot.selectedPrintPosition;
          state.lastManagedPrintPositionId =
            snapshot.lastManagedPrintPositionId;
          state.previousManagedPrintPositionId =
            snapshot.previousManagedPrintPositionId;
        }
      });
    },
  },
});

export const attachActionSubscriber = (): (() => void) =>
  useStore().$onAction(({ name, store, args, after, onError }) => {
    const shapeStore = useShapeStore();
    const notificationStore = useNotificationStore();

    after(async (_result: unknown) => {
      if (name === 'deletePrintPosition') {
        const deletedPositionId = args[0];

        /**
         * Remove all logos assigned to this position
         */
        const affectedShapes = (shape: AnnotationShape) =>
          shapeStore.positionShapeMap.some(
            (psm) => psm.logoId === shape.id && psm.id === deletedPositionId
          );

        // Find shapes to delete
        const shapesToDelete = shapeStore.shapes.filter(affectedShapes);
        const artworkShapesToDelete = shapesToDelete.filter(
          (s) => s.kind === 'Logo'
        );
        const textShapesToDelete = shapesToDelete.filter(
          (s) => s.kind === 'Text'
        );
        textShapesToDelete.forEach((text) =>
          shapeStore.removeTextPayload(text.id, false)
        );
        artworkShapesToDelete.forEach(async (shapeToDelete) => {
          await shapeStore.removeArtworkPayload(shapeToDelete.id, false); // shapeToDelete.id == personalizationArtworkId
        });
      }

      if (name === 'setUnavailablePrintPositions') {
        /**
         * If we have previously managed some print position in any way
         * either by adding it or editing it, make that one active in the
         * dropdown list
         */
        const lastInteractedWith = store.unavailablePrintPositions?.find(
          (position) => position.positionId === store.lastManagedPrintPositionId
        );

        /**
         * lastInteractedWith should track the last edited or last selected
         * position
         */
        if (lastInteractedWith) {
          store.setActivePrintPosition(lastInteractedWith);
        } else {
          console.warn(
            'no lastInteractedWith, setting the first available with lowest sequence number'
          );
          if (store.unavailablePrintPositions.length > 0) {
            store.setActivePrintPosition(store.unavailablePrintPositions[0]);
          }
        }
      }

      if (name === 'setUnavailablePrintPositions') {
        notificationStore.revalidateEmptyPrintPositions();
      }
    });

    onError((error) => {
      console.error(`[ error ]: "${name}".\nError: ${error}.`);
    });
  });
